import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/useSession';
import { useOrgInfo } from '../auth/use-org-info/use-org-info';
import { useApiUrl } from '../config/useApiUrl';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'https://chat.volaby.org';
const POLL_INTERVAL_MS = 30000;

interface ChatUnreadValue {
  total: number;
}

const ChatUnreadContext = createContext<ChatUnreadValue>({ total: 0 });

export const useChatUnread = (): ChatUnreadValue => useContext(ChatUnreadContext);

export const ChatUnreadProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useSession();
  const org = useOrgInfo();
  const apiUrl = useApiUrl();
  const [chatToken, setChatToken] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const originalTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.token || !org?.chatEnabled) {
      setChatToken(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/vm/chat/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `JWT ${user.token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.data?.chatToken) {
          setChatToken(data.data.chatToken);
        }
      } catch {
        // best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.token, org?.chatEnabled, apiUrl]);

  const fetchTotal = useCallback(async () => {
    if (!chatToken) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/api/conversations/unread-total`, {
        headers: { Authorization: `JWT ${chatToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTotal(typeof data.total === 'number' ? data.total : 0);
    } catch {
      // best-effort
    }
  }, [chatToken]);

  useEffect(() => {
    if (!chatToken) {
      setTotal(0);
      return undefined;
    }
    fetchTotal();
    const id = window.setInterval(fetchTotal, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [chatToken, fetchTotal]);

  useEffect(() => {
    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title.replace(/^\(\d+\)\s*/, '');
    }
    const base = originalTitleRef.current;
    document.title = total > 0 ? `(${total}) ${base}` : base;
  }, [total]);

  return <ChatUnreadContext.Provider value={{ total }}>{children}</ChatUnreadContext.Provider>;
};
